import { useEffect } from 'react';
import { useLatestRef } from '@circlefin/hooks';

/**
 * Type guard to ensure valid event data.
 */ function isValidEventData(data) {
    return typeof data === 'object' && data !== null && 'type' in data;
}
/**
 * Custom hook to listen for auth-web iFrame events.
 * @param iFrameRef - React ref for iFrame element.
 * @param callbacks - Object containing callback events.
 * @example
 * ```
 * usePostMessageListener(iFrameRef, {
 *  onIsLoaded: () => console.log('Is loaded')
 * })
 * ```
 */ const usePostMessageListener = (iFrameRef, callbacks)=>{
    const callbacksRef = useLatestRef(callbacks);
    useEffect(()=>{
        const abortController = new AbortController();
        // TODO: This must be fixed, as it's a security concern.
        // eslint-disable-next-line sonarjs/post-message
        window.addEventListener('message', (event)=>{
            if (!iFrameRef.current || event.source !== iFrameRef.current.contentWindow) {
                return; // Unknown source; silently ignore a message.
            }
            const isLocalhostAllowed = window.location.origin.includes('localhost') && event.origin.includes('localhost');
            const isAllowedOrigin = event.origin.endsWith('.circle.com') || isLocalhostAllowed;
            if (!isAllowedOrigin) {
                return; // Unknown origin; silently ignore message.
            }
            if (abortController.signal.aborted) {
                return;
            }
            const { current: { onIsLoaded, onAuthRedirect, onPromoCodeAdded, onSignUpClicked, onAnalyticsLoaded } } = callbacksRef;
            const data = event.data;
            if (!isValidEventData(data)) {
                return; // Invalid message shape, silently ignore a message.
            }
            switch(data.type){
                case 'isLoaded':
                    onIsLoaded?.();
                    break;
                case 'authRedirect':
                    if (typeof data.value === 'string') {
                        onAuthRedirect?.(data.value);
                    }
                    break;
                case 'signUpClicked':
                    onSignUpClicked?.(typeof data.value === 'string' ? data.value : '');
                    break;
                case 'promoCodeAdded':
                    if (typeof data.value === 'string') {
                        onPromoCodeAdded?.(data.value);
                    }
                    break;
                case 'analyticsLoaded':
                    onAnalyticsLoaded?.();
                    break;
            }
        }, {
            signal: abortController.signal
        });
        return ()=>{
            abortController.abort();
        };
    }, [
        callbacksRef,
        iFrameRef
    ]);
};

export { usePostMessageListener };
//# sourceMappingURL=usePostMessageListener.js.map
